import { TeamInvitationTemplate } from "@/components/EmailTemplates/TeamInvitationTemplate";
import { supabase } from "@/integrations/supabase/client";
import { ApiService } from "@/services/api/ApiService";
import { EmailService } from "@/services/communication/EmailService";
import { addPrivateAccessTokenFragment } from "@/lib/privateAccessToken";
import {
  Profile,
  ProfileWithSubscription,
  Roles,
  Team,
  TeamData,
  TeamMember,
  UserData,
} from "@/types/auth/auth";
import { UserUsageSummaryMap } from "@/types/stats";
import type { InboxFilters } from "@/types/conversation/conversation";
import { ImpersonationService } from "./ImpersonationService";

export class UserService {
  /**
   * Get the current user, respecting impersonation if active.
   * Use this instead of supabase.auth.getUser() to support admin impersonation.
   */
  static async getUser() {
    const impersonationState = ImpersonationService.getImpersonationState();

    if (impersonationState) {
      return {
        data: {
          user: {
            id: impersonationState.impersonatedUserId,
            email: `impersonated_${impersonationState.impersonatedUserId}@local`,
            app_metadata: { provider: "impersonation" },
            user_metadata: {
              impersonating: true,
              real_user_id: impersonationState.adminUserId,
            },
            aud: "authenticated",
            created_at: new Date().toISOString(),
            confirmation_sent_at: null,
            email_confirmed_at: null,
            phone_confirmed_at: null,
            is_super_admin: false,
            role: "authenticated",
            last_sign_in_at: new Date().toISOString(),
            identities: null,
          } as any,
        },
        error: null,
      };
    }

    return await supabase.auth.getUser();
  }

  static async getUserData(userId: string): Promise<UserData> {
    // Check for active impersonation
    const impersonatedUserId = ImpersonationService.getImpersonatedUserId();
    const actualUserId = impersonatedUserId || userId;

    // Fetch profile via API (includes team data and subscriptions)
    const profileData = await ApiService.invoke<any>({
      resource: "profiles",
      action: "get",
      id: actualUserId,
    });

    let profile = profileData.profile as Profile & { team?: Team };
    let team: Team | null = null;

    if (profile) {
      // Handle role impersonation (only allow if user is internal_admin)
      const urlParams = new URLSearchParams(window.location.search);
      const impersonateRole = urlParams.get("impersonate_role");

      if (impersonateRole && profile.role === Roles.Admin) {
        profile = {
          ...profile,
          role: impersonateRole,
          originalRole: profile.role,
        };
        console.log(`🎭 Admin impersonating as: ${impersonateRole}`);
      }

      // Add impersonation metadata if active
      if (impersonatedUserId) {
        profile = {
          ...profile,
          isImpersonating: true,
          originalUserId: userId,
        } as any;
        console.log(
          `🎭 Admin impersonating user: ${profile.full_name} (${profile.email})`,
        );
      }

      // Check if user has a team via profile.team_id
      if (profile.team) {
        team = profile.team;
      } else {
        // Fallback: check if user owns a team
        const { data: ownedTeam, error: teamError } = await supabase
          .from("team")
          .select("*")
          .eq("owner_id", actualUserId)
          .maybeSingle();

        if (teamError && teamError.code !== "PGRST116") {
          console.error("Error fetching owned team:", teamError);
          throw teamError;
        }

        team = ownedTeam;
      }

      // Remove the nested team data from profile to avoid confusion
      const { team: _, ...cleanProfile } = profile;
      profile = cleanProfile;
    }

    return {
      profile: profile || null,
      team: team || null,
    };
  }

  static async getProfiles(excludeRole?: string): Promise<Profile[]> {
    const response = await ApiService.invoke<any>({
      resource: "profiles",
      action: "all",
      data: {
        limit: 500,
        offset: 0,
        ...(excludeRole && { filters: { role: excludeRole } }),
      },
    });

    // Filter out excludeRole from the results
    const profiles = response.profiles || [];
    if (excludeRole) {
      return profiles.filter((p: Profile) => p.role !== excludeRole);
    }

    return profiles;
  }

  static async searchProfiles(searchTerm: string, limit = 20): Promise<Profile[]> {
    const response = await ApiService.invoke<{ profiles?: Profile[] }>({
      resource: "profiles",
      action: "list",
      data: { limit, offset: 0, filters: { searchTerm } },
    });
    return response.profiles || [];
  }

  static async getProfilesWithSubscriptions(
    excludeRole?: string,
  ): Promise<ProfileWithSubscription[]> {
    const response = await ApiService.invoke<any>({
      resource: "profiles",
      action: "all",
      data: {
        limit: 500,
        offset: 0,
      },
    });

    const profiles = response.profiles || [];

    // Filter out excludeRole and map profiles with subscriptions
    return profiles
      .filter((p: Profile) => !excludeRole || p.role !== excludeRole)
      .map((profile: any) => ({
        ...profile,
        subscription: profile.subscription || null,
      })) as ProfileWithSubscription[];
  }

  static async updateProfile(
    userId: string,
    updates: Partial<Profile>,
  ): Promise<Profile> {
    const profileData = await ApiService.invoke<any>({
      resource: "profiles",
      action: "update",
      id: userId,
      data: updates,
    });

    return profileData.profile as Profile;
  }

  static async getTeamData(
    teamId: number,
    ownerId: string,
  ): Promise<TeamData | null> {
    try {
      const response = await ApiService.invoke<any>({
        resource: "teams",
        action: "get",
        id: teamId,
      });

      return {
        ...response.team,
        members: response.members || [],
      };
    } catch (error) {
      console.error("Error fetching team:", error);
      return null;
    }
  }

  static async inviteMember(
    inviteEmail: string,
    team: Team,
    inviterProfile: Profile | null,
    userId: string,
  ): Promise<TeamMember> {
    const response = await ApiService.invoke<{
      invitation: { id: string; team_id: number; email: string };
    }>({
      resource: "teams",
      action: "invite",
      id: team.id,
      data: { email: inviteEmail },
    });

    // Send invitation email
    const canonicalInviteEmail = response.invitation.email;
    const signupUrl = new URL("/signup", window.location.origin);
    signupUrl.searchParams.set("team_id", String(response.invitation.team_id));
    signupUrl.searchParams.set("email", canonicalInviteEmail);
    addPrivateAccessTokenFragment(signupUrl, response.invitation.id);
    const capabilitySignupUrl = signupUrl.toString();
    const emailHtml = TeamInvitationTemplate({
      inviteeEmail: canonicalInviteEmail,
      inviterName: inviterProfile?.full_name,
      signupUrl: capabilitySignupUrl,
    });
    const emailText = `${inviterProfile?.full_name ? `${inviterProfile.full_name} has` : "You have been"} invited you to join a team on Help Genie.\n\nAccept your invitation: ${capabilitySignupUrl}\n\nThis invitation was sent to ${canonicalInviteEmail}.`;

    await EmailService.sendEmail(
      canonicalInviteEmail,
      "Team Invitation - Help Genie",
      emailHtml,
      emailText,
      {
        teamInvitationId: response.invitation.id,
        teamId: response.invitation.team_id,
      },
    );

    return {
      id: response.invitation.id,
      full_name: null,
      email: canonicalInviteEmail,
      avatar_url: null,
      role: "Invited",
    };
  }

  static async acceptTeamInvitation(
    teamId: string,
    invitationId: string,
  ): Promise<{ teamId: number; accepted: boolean }> {
    return ApiService.invoke<{ teamId: number; accepted: boolean }>({
      resource: "teams",
      action: "accept-invite",
      id: teamId,
      data: { invitationId },
    });
  }

  static async createTeam(
    teamName: string,
    teamBrand: string,
    ownerId: string,
  ): Promise<Team> {
    const response = await ApiService.invoke<any>({
      resource: "teams",
      action: "create",
      data: {
        name: teamName,
        brand: teamBrand,
      },
    });

    return response.team as Team;
  }

  static async updateTeam(
    teamId: number,
    teamName: string,
    teamBrand: string,
  ): Promise<void> {
    await ApiService.invoke<any>({
      resource: "teams",
      action: "update",
      id: teamId,
      data: {
        name: teamName,
        brand: teamBrand,
      },
    });
  }

  static async removeTeamMember(
    teamId: number,
    memberId: string,
    removedBy: string,
  ): Promise<void> {
    await ApiService.invoke<any>({
      resource: "teams",
      action: "remove-member",
      id: teamId,
      data: { memberId },
    });
  }

  static async cancelInvitation(
    invitationId: string,
    teamId: number,
  ): Promise<void> {
    await ApiService.invoke<any>({
      resource: "teams",
      action: "cancel-invite",
      id: invitationId,
      data: { teamId },
    });
  }

  static async resendInvitation(
    invitationId: string,
    teamId: number,
    _email: string,
    inviterProfile: Profile | null,
  ): Promise<void> {
    const response = await ApiService.invoke<{
      invitation: { id: string; team_id: number; email: string };
    }>({
      resource: "teams",
      action: "resend-invite",
      id: invitationId,
      data: { teamId },
    });

    const canonicalInviteEmail = response.invitation.email;
    const signupUrl = new URL("/signup", window.location.origin);
    signupUrl.searchParams.set("team_id", String(response.invitation.team_id));
    signupUrl.searchParams.set("email", canonicalInviteEmail);
    addPrivateAccessTokenFragment(signupUrl, response.invitation.id);
    const capabilitySignupUrl = signupUrl.toString();
    const emailHtml = TeamInvitationTemplate({
      inviteeEmail: canonicalInviteEmail,
      inviterName: inviterProfile?.full_name,
      signupUrl: capabilitySignupUrl,
    });
    const emailText = `${inviterProfile?.full_name ? `${inviterProfile.full_name} has` : "You have been"} invited you to join a team on Help Genie.\n\nAccept your invitation: ${capabilitySignupUrl}\n\nThis invitation was sent to ${canonicalInviteEmail}.`;

    await EmailService.sendEmail(
      canonicalInviteEmail,
      "Team Invitation - Help Genie",
      emailHtml,
      emailText,
      {
        teamInvitationId: response.invitation.id,
        teamId: response.invitation.team_id,
      },
    );
  }

  static async updateUserProfile(
    userId: string,
    updates: Partial<Profile>,
  ): Promise<Profile> {
    const profileData = await ApiService.invoke<any>({
      resource: "profiles",
      action: "update",
      id: userId,
      data: updates,
    });

    return profileData.profile as Profile;
  }

  static async getInboxFilters(): Promise<InboxFilters | null> {
    const response = await ApiService.invoke<{ filters: InboxFilters | null }>({
      resource: "profiles",
      action: "get-inbox-filters",
    });
    return response?.filters ?? null;
  }

  static async saveInboxFilters(filters: InboxFilters): Promise<InboxFilters> {
    const response = await ApiService.invoke<{ filters: InboxFilters }>({
      resource: "profiles",
      action: "save-inbox-filters",
      data: filters,
    });
    return response.filters;
  }

  static async updateUserPassword(
    userId: string,
    password: string,
  ): Promise<void> {
    await ApiService.invoke<any>({
      resource: "profiles",
      action: "update",
      id: userId,
      data: { password },
    });
  }

  static async generateSsoToken(opts?: {
    impersonatedUserId?: string;
    adminUserId?: string;
  }): Promise<string> {
    const response = await ApiService.invoke<{ token: string }>({
      resource: "profiles",
      action: "generate-sso-token",
      data: opts?.impersonatedUserId
        ? {
            impersonatedUserId: opts.impersonatedUserId,
            adminUserId: opts.adminUserId,
          }
        : undefined,
    });
    return response.token;
  }

  static async generateSyncConsentLink(
    email: string,
    connection: string,
  ): Promise<string> {
    const response = await ApiService.invoke<{ url: string }>({
      resource: "profiles",
      action: "generate-sync-consent-link",
      data: { email, connection },
    });
    return response.url;
  }

  static async getUsageSummary(
    userIds: string[],
  ): Promise<UserUsageSummaryMap> {
    if (userIds.length === 0) return {};

    return await ApiService.invoke<UserUsageSummaryMap>({
      resource: "profiles",
      action: "usage-summary",
      data: { userIds },
    });
  }
}
